/**
 * Compare Tab Component
 * Tab So Sánh - giữ nguyên logic MapTool cũ
 */
import { useState, useEffect, useRef } from 'react'
import api from '../../services/api'
import mapSessionService from '../../services/mapSessionService'
import {
  HiOutlineMap,
  HiOutlineSearch,
  HiOutlinePhotograph,
  HiOutlineShare,
  HiOutlineAdjustments,
  HiOutlineBookmark,
  HiOutlineLink,
  HiOutlineTrash,
  HiOutlineFolder,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import LeafletComparisonMap from '../map/LeafletComparisonMap'
import html2canvas from 'html2canvas'

function CompareTab() {
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [nearbyProjects, setNearbyProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingNearby, setLoadingNearby] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [radiusKm, setRadiusKm] = useState(5)
  const [showFilters, setShowFilters] = useState(false)
  const [segmentFilter, setSegmentFilter] = useState('all')
  const [savedSessions, setSavedSessions] = useState([])
  const [showSavedSessions, setShowSavedSessions] = useState(false)
  const [savingSession, setSavingSession] = useState(false)
  const mapContainerRef = useRef(null)

  useEffect(() => {
    loadProjects()
    loadSavedSessions()
  }, [])

  const loadSavedSessions = async () => {
    try {
      const response = await mapSessionService.getSessions({ per_page: 10 })
      if (response.status === 'success') {
        setSavedSessions(response.data || [])
      }
    } catch (error) {
      console.error('Failed to load sessions:', error)
    }
  }

  const handleSaveSession = async () => {
    if (!selectedProject || nearbyProjects.length === 0) {
      toast.error('Chọn dự án và tìm dự án lân cận trước')
      return
    }

    setSavingSession(true)
    try {
      const response = await mapSessionService.createSession({
        center_project_id: selectedProject.id,
        nearby_project_ids: nearbyProjects.map((p) => p.id),
        radius_km: radiusKm,
      })

      if (response.status === 'success') {
        toast.success('Đã lưu phiên so sánh!')
        loadSavedSessions()

        // Copy share link
        const shareUrl = `${window.location.origin}/map/share/${response.data.session_code}`
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Đã copy link chia sẻ!', { duration: 3000 })
      }
    } catch (error) {
      console.error('Failed to save session:', error)
      toast.error('Lưu thất bại')
    } finally {
      setSavingSession(false)
    }
  }

  const handleLoadSession = async (session) => {
    try {
      const response = await mapSessionService.getSession(session.id)
      if (response.status === 'success') {
        const data = response.data
        setSelectedProject(data.center_project)
        setNearbyProjects(data.nearby_projects || [])
        setRadiusKm(data.radius_km)
        setShowSavedSessions(false)
        toast.success('Đã tải phiên so sánh')
      }
    } catch (error) {
      console.error('Failed to load session:', error)
      toast.error('Không thể tải phiên')
    }
  }

  const handleDeleteSession = async (sessionId) => {
    if (!confirm('Xóa phiên so sánh này?')) return

    try {
      await mapSessionService.deleteSession(sessionId)
      toast.success('Đã xóa')
      loadSavedSessions()
    } catch (error) {
      console.error('Failed to delete session:', error)
      toast.error('Xóa thất bại')
    }
  }

  const handleCopyShareLink = async (sessionCode) => {
    const shareUrl = `${window.location.origin}/map/share/${sessionCode}`
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Đã copy link chia sẻ!')
    } catch (error) {
      toast.error('Không thể copy')
    }
  }

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects', {
        params: { per_page: 100 },
      })
      if (response.data.status === 'success') {
        setProjects(response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to load projects:', error)
      toast.error('Không thể tải danh sách dự án')
    } finally {
      setLoading(false)
    }
  }

  const loadNearbyProjects = async (project, radius = radiusKm) => {
    if (!project.latitude || !project.longitude) {
      toast.error('Dự án này chưa có tọa độ')
      return
    }

    setLoadingNearby(true)
    try {
      const response = await api.get('/projects/nearby', {
        params: {
          lat: project.latitude,
          lng: project.longitude,
          radius: radius,
          exclude_id: project.id,
        },
      })
      if (response.data.status === 'success') {
        setNearbyProjects(response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to load nearby projects:', error)
      toast.error('Không thể tải dự án lân cận')
    } finally {
      setLoadingNearby(false)
    }
  }

  const handleSelectProject = (project) => {
    setSelectedProject(project)
    loadNearbyProjects(project)
  }

  const handleRadiusChange = (newRadius) => {
    setRadiusKm(newRadius)
    if (selectedProject) {
      loadNearbyProjects(selectedProject, newRadius)
    }
  }

  const handleExportImage = async () => {
    if (!mapContainerRef.current) return

    try {
      toast.loading('Đang xuất ảnh...', { id: 'export' })
      const canvas = await html2canvas(mapContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
      })

      const link = document.createElement('a')
      link.download = `map-${selectedProject?.name || 'comparison'}-${Date.now()}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()

      toast.success('Đã xuất ảnh thành công!', { id: 'export' })

      // Log activity
      api
        .post('/activities', {
          type: 'create_map',
          reference_type: 'project',
          reference_id: selectedProject?.id,
          metadata: { action: 'export_image' },
        })
        .catch(() => {})
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Xuất ảnh thất bại', { id: 'export' })
    }
  }

  const handleShareToCustomer = async () => {
    if (!selectedProject) return

    const comparisonText = generateComparisonText()

    try {
      await navigator.clipboard.writeText(comparisonText)
      toast.success('Đã copy nội dung so sánh!')

      // Log activity
      api
        .post('/activities', {
          type: 'share_map',
          reference_type: 'project',
          reference_id: selectedProject.id,
          metadata: { nearby_count: nearbyProjects.length },
        })
        .catch(() => {})
    } catch (error) {
      toast.error('Không thể copy')
    }
  }

  const generateComparisonText = () => {
    if (!selectedProject) return ''

    let text = `📍 SO SÁNH GIÁ DỰ ÁN\n\n`
    text += `🏢 DỰ ÁN TRUNG TÂM: ${selectedProject.name}\n`
    text += `📍 Vị trí: ${selectedProject.address || selectedProject.location}\n`
    text += `💰 Giá: ${formatPrice(selectedProject.price_from)} - ${formatPrice(selectedProject.price_to)}\n`
    if (selectedProject.price_per_m2) {
      text += `📐 Giá/m²: ${(selectedProject.price_per_m2 / 1000000).toFixed(0)} triệu/m²\n`
    }
    text += `\n━━━━━━━━━━━━━━━━━━━━\n\n`
    text += `📊 CÁC DỰ ÁN LÂN CẬN (bán kính ${radiusKm}km):\n\n`

    nearbyProjects.forEach((p, index) => {
      text += `${index + 1}. ${p.name}\n`
      text += `   📍 Khoảng cách: ${p.distance_km}km\n`
      if (p.price_per_m2) {
        text += `   💰 Giá/m²: ${(p.price_per_m2 / 1000000).toFixed(0)} triệu/m²\n`
      }
      text += `\n`
    })

    text += `\n━━━━━━━━━━━━━━━━━━━━\n`
    text += `📱 Liên hệ để được tư vấn chi tiết!`

    return text
  }

  const formatPrice = (price) => {
    if (!price) return '-'
    if (price >= 1000000000) {
      return `${(price / 1000000000).toFixed(1)} tỷ`
    }
    return `${(price / 1000000).toFixed(0)} triệu`
  }

  const getSegmentLabel = (segment) => {
    const labels = {
      cao_cap: 'Cao cấp',
      trung_cap: 'Trung cấp',
      binh_dan: 'Bình dân',
      nghi_duong: 'Nghỉ dưỡng',
    }
    return labels[segment] || segment
  }

  const filteredProjects = projects.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesSegment = segmentFilter === 'all' || p.segment === segmentFilter
    return matchesSearch && matchesSegment
  })

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <HiOutlineMap className="w-5 h-5 mr-2 text-primary-500" />
            So sánh dự án
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Chọn dự án trung tâm và so sánh với các dự án xung quanh
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Radius selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Bán kính:</span>
            <div className="flex gap-1">
              {[3, 5, 10, 15].map((r) => (
                <button
                  key={r}
                  onClick={() => handleRadiusChange(r)}
                  className={clsx(
                    'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                    radiusKm === r
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {r}km
                </button>
              ))}
            </div>
          </div>

          {/* Save & Load buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowSavedSessions(!showSavedSessions)}
              className={clsx(
                'btn-sm',
                showSavedSessions ? 'btn-primary' : 'btn-outline'
              )}
            >
              <HiOutlineFolder className="w-4 h-4 mr-1" />
              Đã lưu ({savedSessions.length})
            </button>
            <button
              onClick={handleSaveSession}
              disabled={savingSession || !selectedProject}
              className="btn-sm btn-primary"
            >
              {savingSession ? (
                <span className="spinner w-4 h-4 mr-1" />
              ) : (
                <HiOutlineBookmark className="w-4 h-4 mr-1" />
              )}
              Lưu & Chia sẻ
            </button>
          </div>
        </div>
      </div>

      {/* Saved Sessions Panel */}
      {showSavedSessions && savedSessions.length > 0 && (
        <div className="card mb-6 p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Các phiên đã lưu</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {savedSessions.map((session) => (
              <div
                key={session.id}
                className="p-3 bg-gray-50 rounded-lg flex items-center justify-between"
              >
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm truncate">
                    {session.name}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {session.center_project?.name} • {session.nearby_count} dự án
                  </p>
                </div>
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={() => handleCopyShareLink(session.session_code)}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Copy link chia sẻ"
                  >
                    <HiOutlineLink className="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => handleLoadSession(session)}
                    className="p-1.5 hover:bg-primary-100 rounded-lg transition-colors"
                    title="Mở phiên này"
                  >
                    <HiOutlineFolder className="w-4 h-4 text-primary-500" />
                  </button>
                  <button
                    onClick={() => handleDeleteSession(session.id)}
                    className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                    title="Xóa"
                  >
                    <HiOutlineTrash className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Project selector */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Chọn dự án trung tâm</h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={clsx(
                    'p-2 rounded-lg transition-colors',
                    showFilters
                      ? 'bg-primary-100 text-primary-600'
                      : 'hover:bg-gray-100'
                  )}
                >
                  <HiOutlineAdjustments className="w-5 h-5" />
                </button>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Phân khúc
                  </label>
                  <select
                    value={segmentFilter}
                    onChange={(e) => setSegmentFilter(e.target.value)}
                    className="input text-sm"
                  >
                    <option value="all">Tất cả</option>
                    <option value="cao_cap">Cao cấp</option>
                    <option value="trung_cap">Trung cấp</option>
                    <option value="binh_dan">Bình dân</option>
                    <option value="nghi_duong">Nghỉ dưỡng</option>
                  </select>
                </div>
              )}

              <div className="relative">
                <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Tìm dự án..."
                  className="input pl-10"
                />
              </div>
            </div>

            <div className="max-h-[350px] overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredProjects.length > 0 ? (
                <div className="divide-y">
                  {filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleSelectProject(project)}
                      className={clsx(
                        'w-full p-4 text-left hover:bg-gray-50 transition-colors',
                        selectedProject?.id === project.id &&
                          'bg-primary-50 border-l-4 border-primary-500'
                      )}
                    >
                      <h3 className="font-medium text-gray-900 text-sm">
                        {project.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>
                          {typeof project.location === 'object'
                            ? project.location?.name
                            : project.location}
                        </span>
                        <span>•</span>
                        <span
                          className={clsx(
                            'px-1.5 py-0.5 rounded text-[10px] font-medium',
                            project.segment === 'cao_cap' &&
                              'bg-purple-100 text-purple-700',
                            project.segment === 'trung_cap' &&
                              'bg-blue-100 text-blue-700',
                            project.segment === 'binh_dan' &&
                              'bg-green-100 text-green-700',
                            project.segment === 'nghi_duong' &&
                              'bg-amber-100 text-amber-700'
                          )}
                        >
                          {getSegmentLabel(project.segment)}
                        </span>
                      </div>
                      <p className="text-sm text-primary-600 font-medium mt-1">
                        {formatPrice(project.price_from)} -{' '}
                        {formatPrice(project.price_to)}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  Không tìm thấy dự án
                </div>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="card mt-4 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Chú thích</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-gray-600">Dự án trung tâm</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-purple-500" />
                <span className="text-gray-600">Cao cấp</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-gray-600">Trung cấp</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-gray-600">Bình dân</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-gray-600">Nghỉ dưỡng</span>
              </div>
            </div>
          </div>
        </div>

        {/* Map and comparison */}
        <div className="lg:col-span-2 space-y-6">
          {/* Leaflet Map */}
          <div className="card overflow-hidden" ref={mapContainerRef}>
            <div className="h-[350px] lg:h-[400px]">
              {selectedProject ? (
                <LeafletComparisonMap
                  centerProject={selectedProject}
                  nearbyProjects={nearbyProjects}
                  radiusKm={radiusKm}
                  onProjectClick={(project) => {
                    if (project.id !== selectedProject.id) {
                      handleSelectProject(project)
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <div className="text-center text-gray-400">
                    <HiOutlineMap className="w-16 h-16 mx-auto mb-3" />
                    <p>Chọn dự án để hiển thị bản đồ</p>
                  </div>
                </div>
              )}
            </div>

            {selectedProject && (
              <div className="p-4 border-t border-gray-100 flex gap-3">
                <button onClick={handleExportImage} className="btn-outline flex-1">
                  <HiOutlinePhotograph className="w-5 h-5 mr-2" />
                  Xuất ảnh
                </button>
                <button
                  onClick={handleShareToCustomer}
                  className="btn-primary flex-1"
                >
                  <HiOutlineShare className="w-5 h-5 mr-2" />
                  Gửi cho khách
                </button>
              </div>
            )}
          </div>

          {/* Comparison table */}
          {selectedProject && (
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  {loadingNearby ? (
                    <span className="flex items-center">
                      <span className="spinner w-4 h-4 mr-2" />
                      Đang tải...
                    </span>
                  ) : (
                    `So sánh với ${nearbyProjects.length} dự án lân cận (${radiusKm}km)`
                  )}
                </h3>
              </div>

              {nearbyProjects.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">
                          Dự án
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">
                          Khoảng cách
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">
                          Giá/m²
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">
                          Phân khúc
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">
                          So sánh
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {/* Center project */}
                      <tr className="bg-primary-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-primary-700">
                            {selectedProject.name}
                          </span>
                          <span className="ml-2 badge-primary text-[10px]">
                            TRUNG TÂM
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">-</td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {selectedProject.price_per_m2
                            ? `${(selectedProject.price_per_m2 / 1000000).toFixed(0)} tr/m²`
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {getSegmentLabel(selectedProject.segment)}
                        </td>
                        <td className="px-4 py-3">-</td>
                      </tr>

                      {/* Nearby projects */}
                      {nearbyProjects.map((project) => {
                        const centerPrice = selectedProject.price_per_m2 || 0
                        const projectPrice = project.price_per_m2 || 0
                        const priceDiff =
                          centerPrice && projectPrice
                            ? (
                                ((projectPrice - centerPrice) / centerPrice) *
                                100
                              ).toFixed(0)
                            : null

                        return (
                          <tr key={project.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {project.name}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {project.distance_km} km
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {project.price_per_m2
                                ? `${(project.price_per_m2 / 1000000).toFixed(0)} tr/m²`
                                : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={clsx(
                                  'px-2 py-1 rounded text-xs font-medium',
                                  project.segment === 'cao_cap' &&
                                    'bg-purple-100 text-purple-700',
                                  project.segment === 'trung_cap' &&
                                    'bg-blue-100 text-blue-700',
                                  project.segment === 'binh_dan' &&
                                    'bg-green-100 text-green-700',
                                  project.segment === 'nghi_duong' &&
                                    'bg-amber-100 text-amber-700'
                                )}
                              >
                                {getSegmentLabel(project.segment)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {priceDiff !== null && (
                                <span
                                  className={clsx(
                                    'text-sm font-medium',
                                    parseInt(priceDiff) > 0
                                      ? 'text-red-600'
                                      : 'text-green-600'
                                  )}
                                >
                                  {parseInt(priceDiff) > 0 ? '+' : ''}
                                  {priceDiff}%
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : !loadingNearby ? (
                <div className="p-8 text-center text-gray-500">
                  Không tìm thấy dự án lân cận trong bán kính {radiusKm}km
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CompareTab
